import React, { useState, useEffect, useCallback } from "react";
import { Box, IconButton } from "@mui/material";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

/**
 * Componente LightBox para ampliar imagens
 * 
 * @param {Object} props
 * @param {React.RefObject} props.contentRef - Ref do container que contém as imagens
 * @param {string} [props.tooltip] - Texto do tooltip ao passar o mouse nas imagens
 * @param {string} [props.imageErrorText] - Texto de erro quando imagem não carrega
 * @param {any} [props.dependency] - Dependência para re-executar o setup (ex: article.content)
 * 
 * @example
 * // No componente pai:
 * this.contentRef = React.createRef()
 * 
 * // No render:
 * <div ref={this.contentRef} dangerouslySetInnerHTML={{ __html: article.content }} />
 * <LightBoxModal contentRef={this.contentRef} dependency={article.content} />
 */
export default function LightBoxModal({
  contentRef,
  tooltip = "Clique para ampliar",
  imageErrorText = "Imagem indisponível",
  dependency = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState("");

  const handleImageClick = useCallback((e) => {
    e.preventDefault();
    setImageSrc(e.target.src);
    setIsOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
    setImageSrc("");
  }, []);

  // Fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeLightbox();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeLightbox]);

  // Setup dos listeners nas imagens e ajuste de iframes
  useEffect(() => {
    const createErrorPlaceholder = (img) => {
      // Verifica se já existe um placeholder para esta imagem
      if (img.dataset.errorHandled === "true") return;
      img.dataset.errorHandled = "true";

      // Cria um container para substituir a imagem quebrada
      const placeholder = document.createElement("div");
      placeholder.style.cssText = `
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background-color: #f5f5f5;
        border: 1px dashed #ccc;
        border-radius: 8px;
        color: #666;
        font-size: 14px;
        min-width: 200px;
        min-height: 100px;
        text-align: center;
        gap: 8px;
      `;

      // Ícone de imagem quebrada
      const icon = document.createElement("span");
      icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
        <line x1="3" y1="3" x2="21" y2="21" stroke="#d32f2f" stroke-width="2"/>
      </svg>`;

      // Texto de erro
      const text = document.createElement("span");
      text.textContent = imageErrorText;

      placeholder.appendChild(icon);
      placeholder.appendChild(text);

      // Substitui a imagem pelo placeholder
      img.style.display = "none";
      img.parentNode.insertBefore(placeholder, img.nextSibling);
    };

    const setupImageListener = (img) => {
      // Verifica se a imagem carregou corretamente
      const isImageValid = img.complete && img.naturalWidth > 0 && img.naturalHeight > 0;
      
      if (isImageValid) {
        img.style.cursor = "pointer";
        img.title = tooltip;
        img.removeEventListener("click", handleImageClick);
        img.addEventListener("click", handleImageClick);
      } else {
        // Cria placeholder para imagens inválidas
        createErrorPlaceholder(img);
        img.removeEventListener("click", handleImageClick);
      }
    };

    const setupListeners = () => {
      if (contentRef?.current) {
        // Setup para imagens
        const images = contentRef.current.querySelectorAll("img");
        images.forEach((img) => {
          if (img.complete) {
            // Imagem já carregou (ou falhou)
            setupImageListener(img);
          } else {
            // Aguardar o carregamento
            img.addEventListener("load", () => setupImageListener(img), { once: true });
            img.addEventListener("error", () => setupImageListener(img), { once: true });
          }
        });

        // Setup para iframes - ajustar width para 100%
        const iframes = contentRef.current.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          iframe.style.width = "100%";
        });
      }
    };

    // Pequeno delay para garantir que o DOM foi atualizado
    const timeoutId = setTimeout(setupListeners, 100);

    return () => {
      clearTimeout(timeoutId);
      // Cleanup dos listeners
      if (contentRef?.current) {
        const images = contentRef.current.querySelectorAll("img");
        images.forEach((img) => {
          img.removeEventListener("click", handleImageClick);
        });
      }
    };
  }, [contentRef, dependency, tooltip, imageErrorText, handleImageClick]);

  if (!isOpen) return null;

  return (
    <Box
      onClick={closeLightbox}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        cursor: "pointer",
      }}
    >
      <IconButton
        onClick={closeLightbox}
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          color: "#fff",
          backgroundColor: "rgba(255,255,255,0.1)",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.2)",
          },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: "24px", fontWeight: "bold" }} />
      </IconButton>
      <img
        src={imageSrc}
        alt="Imagem ampliada"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          objectFit: "contain",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
          cursor: "default",
        }}
      />
    </Box>
  );
}